import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { FaArrowLeft } from "react-icons/fa"
import "./PostCreatePage.css"
import { TopicSelector } from "@/components/community/TopicSelector"
import { PlaceSelector, type PlaceData } from "@/components/community/PlaceSelector"
import { PlaceSearchModal } from "@/components/community/PlaceSearchModal"
import { ImageUploader } from "@/components/community/ImageUploader"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import { createPost, fetchTags, selectTags } from "@/features/posts/postsSlice"
import type { PostTagResponse } from "@/types/domain"
import { uploadCommunityImage } from "@/services/api"

const PostCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  
  const tags = useAppSelector(selectTags)

  useEffect(() => {
     void dispatch(fetchTags())
  }, [dispatch])

  const [selectedTag, setSelectedTag] = useState<PostTagResponse | null>(null)
  const [place, setPlace] = useState<PlaceData | null>(null)
  const [images, setImages] = useState<File[]>([])
  const [imageIds, setImageIds] = useState<number[]>([])
  const [title, setTitle] = useState("")
  const [content, setContent] = useState("")
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const [initialLocation, setInitialLocation] = useState<{lat: number, lng: number} | undefined>(undefined)

  const handlePlaceSelect = (selectedPlace: PlaceData) => {
    setPlace(selectedPlace)
    setIsMapModalOpen(false)
  }

  const handleAddImages = async (newFiles: File[]) => {
    // Optimistically update UI
    setImages(prev => [...prev, ...newFiles])

    // Upload images and extract GPS
    for (const file of newFiles) {
        try {
            const metadata = await uploadCommunityImage(file)
            setImageIds(prev => [...prev, metadata.imageId])
            
            // If GPS data exists and no place is selected yet, update initial location for map
            // Only update if latitude is not 0 (silly check but sometimes APIs return 0 for null)
            if (metadata.latitude !== 0 && metadata.longitude !== 0) {
                 setInitialLocation({
                     lat: metadata.latitude,
                     lng: metadata.longitude
                 })
            }
        } catch (error) {
            console.error("Image upload failed", error)
            alert("이미지 업로드 중 오류가 발생했습니다.")
            // Rollback optimistic update if needed, but for now just let it be (actual submission will fail if id missing)
        }
    }
  }

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index))
    setImageIds(prev => prev.filter((_, i) => i !== index)) // Assuming sequential add/remove matches index. Limitation of current ImageUploader
  }

  const handleSubmit = async () => {
    if (!selectedTag) {
      alert("주제를 선택해주세요.")
      return
    }
    if (!title.trim()) {
      alert("제목을 입력해주세요.")
      return
    }
    if (!content.trim()) {
      alert("내용을 입력해주세요.")
      return
    }

    try {
      await dispatch(createPost({
        title,
        content,
        tagNames: [selectedTag.name],
        images,
        imageIds, // Pass pre-uploaded image IDs
        place
      })).unwrap()
      alert("게시글이 등록되었습니다.")
      navigate("/community")
    } catch (err) {
      console.error("Failed to create post:", err)
      alert("게시글 등록에 실패했습니다.")
    }
  }

  // Determine modal mode
  const mapMode = selectedTag?.campingSiteEnabled ? "SELECT_EXISTING" : "SELECT_COORDINATE"

  const handleOpenSearchModal = () => {
      // If we have extracted GPS from image, verify if we should use it.
      // Already set in initialLocation state.
      setIsMapModalOpen(true)
  }

  return (
    <div className="post-create-page">
      <header className="post-create-header">
        <button 
          className="back-button" 
          onClick={() => { void navigate(-1); }}
          type="button"
        >
          <FaArrowLeft />
        </button>
        <h1 className="header-title">글쓰기</h1>
        <button 
          className="submit-button" 
          onClick={() => { void handleSubmit(); }}
          type="button"
        >
          등록
        </button>
      </header>

      <div className="post-create-content">
        <TopicSelector 
          tags={tags}
          selectedTagId={selectedTag?.id ?? null}
          onSelect={setSelectedTag}
        />

        {(selectedTag?.mapEnabled || selectedTag?.campingSiteEnabled) && (
          <PlaceSelector 
            place={place}
            onOpenSearch={handleOpenSearchModal}
            onRemove={() => { setPlace(null); }}
          />
        )}

        <input
          type="text"
          className="post-title-input"
          placeholder="제목을 입력하세요"
          value={title}
          onChange={(e) => { setTitle(e.target.value); }}
        />

        <textarea
          className="post-content-input"
          placeholder="내용을 입력하세요"
          value={content}
          onChange={(e) => { setContent(e.target.value); }}
        />

        {selectedTag?.imageEnabled && (
          <ImageUploader 
            images={images}
            onAdd={(files) => { void handleAddImages(files); }}
            onRemove={handleRemoveImage}
          />
        )}
      </div>

      <PlaceSearchModal 
        isOpen={isMapModalOpen}
        onClose={() => { setIsMapModalOpen(false); }}
        onConfirm={handlePlaceSelect}
        mode={mapMode}
        initialLocation={initialLocation}
      />
    </div>
  )
}

export default PostCreatePage
